import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, Sector } from 'recharts';
import { formatCurrencyAmount, formatNumberValue } from '../../../../../utils/miscFunctions';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../../../states/slices/resourceSlice';
import { MoveLeftIcon, MoveRightIcon } from 'lucide-react';


const ActiveShape: React.FC<{
  cx?: number;
  cy?: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
}> = ({ cx = 0, cy = 0, innerRadius, outerRadius, startAngle, endAngle, fill }) => (
  <g>
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 6}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
    <Sector
      cx={cx}
      cy={cy}
      startAngle={startAngle}
      endAngle={endAngle}
      innerRadius={outerRadius + 8}
      outerRadius={outerRadius + 10}
      fill={fill}
    />
  </g>
);

export interface PolicyStats {
  totalExpiredPolicies: number;
  totalActivePolicies: number;
  countByType: Record<string, number>;
  premiumByType: Record<string, number>;
}


const NonMotorPolicyDistributionChart = ({ nonMotorPolicyDistributionData, isLoadingPolicyDistribution }: INonMotorPolicyDistributionChartProps) => {
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const country = useSelector(getSelectedCountry)
  const selectedCountryIso = country.currencyIso
  const [step, setStep] = useState(0)

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload?.length) {
      const data = payload[0]?.payload;
      return (
        <div className="bg-white dark:bg-gray-900 p-4 border border-gray-200 dark:border-gray-700 shadow-md rounded-md">
          <p className="font-medium text-gray-900 dark:text-white">{data?.type}</p>
          <p className="text-sm text-gray-600 font-bold dark:text-white">Policy: {data?.name}</p>
          <p className="text-sm text-gray-600 dark:text-white">Count: {formatNumberValue(data?.count)}</p>
          <p className="text-sm text-gray-600 dark:text-white">Premium: {formatCurrencyAmount(data?.premium, selectedCountryIso)}</p>
          <p className="text-sm text-gray-600 dark:text-white">Share: {data?.share.toFixed(2)} %</p>
        </div>
      );
    }
    return null;
  };

  // Insurance types and a basic color palette
  const insuranceColors: Record<string, string> = {
    'Home Insurance': '#8884d8',
    'Marine Insurance': '#82ca9d',
    'Fire Insurance': '#ffc658',
    'Life Insurance': '#ff8042',
    'Personal Accident Insurance': '#8dd1e1',
    'Travel Insurance': '#a4de6c',
    'Professional Indemnity Insurance': '#d0ed57',
    'Business Insurance': '#8884d8',
    'Public Liability Insurance': '#83a6ed',
    'Health Insurance': '#8a2be2',
    'Money Insurance': '#2be262',
    'Fidelity Guarantee': '#e2c72b',
    'Goods In Transit': '#e2562b',
    'Asset All Risk': '#2b80e2',
    'Petroleum Bond': '#e22bb7',
    'Fire and Allied Perils Insurance': '#e25c2b',
    'Plant and Machinery': '#2be2c2',
    'Group Personal Accident': '#6c2be2',
    "Workmen's Compensation Policy": '#2b57e2'
  };

  const chartNonMotorPolicyDataPremium =
    nonMotorPolicyDistributionData && nonMotorPolicyDistributionData?.premiumByType &&
    Object?.entries(nonMotorPolicyDistributionData?.premiumByType).map(([type, premium]) => ({
      name: type,
      premium,
      color: insuranceColors[type] || '#8884d8' // Fallback color
    }));


  const chartNonMotorPolicyDataCounts = nonMotorPolicyDistributionData && nonMotorPolicyDistributionData?.countByType &&
    Object?.entries(nonMotorPolicyDistributionData?.countByType).map(([type, count]) => ({
      name: type,
      count
    }));

  const chartCounts = chartNonMotorPolicyDataCounts?.map((item: any) => item.count);
  const chartCountsSum = chartCounts?.reduce((a: number, b: number) => a + b, 0)

  // Transform countByType into chartNonMotorPolicyData array
  const chartNonMotorPolicyData = nonMotorPolicyDistributionData && nonMotorPolicyDistributionData?.countByType &&
    Object?.entries(nonMotorPolicyDistributionData?.countByType).map(([type, count]) => ({
      name: type,
      count,
      color: insuranceColors[type] || '#8884d8', // Fallback color
      premium: chartNonMotorPolicyDataPremium?.find((value: any) => type === value.name)?.premium || 0,
      share: chartCountsSum > 0 ? (count as number / chartCountsSum) * 100 : 0
    }));

  // Transform the data for the chart
  const hasData =
    chartNonMotorPolicyData &&
    chartNonMotorPolicyData?.length > 0 &&
    chartNonMotorPolicyData?.some((item: any) => item.count > 0);

  const chartNonMotorPolicyDataWithCount = chartNonMotorPolicyData?.filter(
    (data: any) => data.count !== 0
  )

  // Loading state
  if (isLoadingPolicyDistribution) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="h-6 bg-gray-200 rounded w-48 animate-pulse mb-2"></div>
            <div className="h-4 bg-gray-100 rounded w-32 animate-pulse"></div>
          </div>
          <div className="w-32 h-8 bg-gray-100 rounded-lg animate-pulse"></div>
        </div>

        <div className="flex-1 flex flex-col">
          <div className="mb-4 flex items-center justify-center">
            <div className="relative">
              <div className="w-32 h-32 border-8 border-gray-200 rounded-full animate-pulse"></div>
              <div className="absolute inset-4 w-24 h-24 border-4 border-gray-100 rounded-full animate-pulse"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-2 mb-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center mx-2">
                <div className="w-2 h-2 bg-gray-200 rounded-full mr-1 animate-pulse"></div>
                <div className="h-3 bg-gray-100 rounded w-20 animate-pulse"></div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center w-full mb-4">
            <div className="flex gap-3">
              <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>

          <div className="mt-auto pt-4 border-t border-gray-200 dark:border-darkBg-border grid grid-cols-3 gap-4">
            <div className="px-0 py-3 bg-gray-100 rounded-lg col-span-1 animate-pulse">
              <div className="h-3 bg-gray-200 rounded w-20 mx-auto mb-1"></div>
              <div className="h-5 bg-gray-300 rounded w-12 mx-auto"></div>
            </div>
            <div className="px-0 py-3 bg-gray-100 rounded-lg col-span-2 animate-pulse">
              <div className="h-3 bg-gray-200 rounded w-24 mx-auto mb-1"></div>
              <div className="h-5 bg-gray-300 rounded w-16 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!hasData) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-2">
              No Non-Motor Policies Found
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              There are no non-motor policy records available for the selected date range.
              Try selecting a different date range or check back later for updates.
            </p>
            <div className="inline-flex items-center px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Try a different date range
            </div>
          </div>
        </div>
      </div>
    );
  }

  const x = 5

  const totalNumberOfPolicyTypes = chartNonMotorPolicyDataWithCount?.length || 0

  const NumberOfSteps =
    chartNonMotorPolicyDataWithCount
      ? Math.ceil?.(totalNumberOfPolicyTypes / x)
      : 0;

  const policyTypeList = () => {
    if (!chartNonMotorPolicyDataWithCount) return [];

    const start = step * x;
    const end = start + x;
    return chartNonMotorPolicyDataWithCount.slice(start, end);
  };


  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex flex-col">
        <div className="h-[300px] mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                activeShape={ActiveShape as any}
                data={chartNonMotorPolicyDataWithCount}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="count"
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}>
                {chartNonMotorPolicyDataWithCount?.map((entry: any, index: number) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    fillOpacity={0.8}
                    stroke="#fff"
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                content={() => {
                  const dataset = policyTypeList() || [];
                  return (
                    <ul className="flex flex-wrap justify-center text-xs text-gray-700 dark:text-darkBg-mainText">
                      {dataset.map((item: any, idx: number) => (
                        <li key={idx} className="mx-2 flex items-center">
                          <span
                            className="inline-block w-2 h-2 rounded-full mr-1"
                            style={{ backgroundColor: item.color }}
                          />
                          {item.name}
                        </li>
                      ))}
                    </ul>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex items-center justify-center w-full mb-4">
          <div className="flex gap-3">
            <MoveLeftIcon
              onClick={() => { step > 0 && setStep(step - 1) }}
              className={`${step === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 cursor-pointer hover:text-gray-700'} transition-all duration-300 scale-100 hover:scale-110`}
            />
            <MoveRightIcon
              onClick={() => { step < NumberOfSteps - 1 && setStep(step + 1) }}
              className={`${step === NumberOfSteps - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 cursor-pointer hover:text-gray-700'} transition-all duration-300 scale-100 hover:scale-110`}
            />
          </div>
        </div>

        <div className="mt-auto pt-4 border-t border-gray-200 dark:border-darkBg-border grid grid-cols-3 gap-4 text-center">
          <div className="px-0 py-3 bg-gray-50 dark:bg-darkBg-border rounded-lg col-span-1">
            <p className="text-xs font-medium text-gray-600 dark:text-darkBg-mainText">Total Policies</p>
            <p className="text-lg font-bold text-gray-900 dark:text-darkBg-mainText">
              {formatNumberValue(
                chartNonMotorPolicyData?.reduce((sum: number, item: any) => sum + item.count, 0)
              )}
            </p>
          </div>
          <div className="px-0 py-3 bg-gray-50 dark:bg-darkBg-border rounded-lg col-span-2">
            <p className="text-xs font-medium text-gray-600 dark:text-darkBg-mainText">Total Premium {selectedCountryIso ? `(${selectedCountryIso})` : ''}</p>
            <p className="text-lg font-bold text-gray-900 dark:text-darkBg-mainText">
              {formatCurrencyAmount(chartNonMotorPolicyData?.reduce((sum: number, item: any) => sum + item.premium, 0), undefined, false)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NonMotorPolicyDistributionChart;


interface INonMotorPolicyDistributionChartProps {
  nonMotorPolicyDistributionData?: PolicyStats
  isLoadingPolicyDistribution: boolean
}
